import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "@/lib/config";
import { Plus, Trash2, Loader2, Upload, X, Pencil } from "lucide-react";

const SPAN_OPTIONS = [
  { value: "normal", label: "Normal" },
  { value: "wide", label: "Wide (spans 2 cols)" },
  { value: "tall", label: "Tall (spans 2 rows)" },
];

const EMPTY = {
  id: null,
  title: "",
  description: "",
  location: "",
  sqft: 1000,
  before_image: "",
  after_image: "",
  span: "normal",
};

// Resize image client-side to keep mongo doc small
function readAndResize(file, maxDim = 1600, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => { img.src = e.target.result; };
    reader.onerror = reject;
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function ImageInput({ value, onChange, label, testid }) {
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Image is too large (8MB max).");
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await readAndResize(f);
      onChange(dataUrl);
    } catch {
      toast.error("Could not process image");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div>
      <Label className="overline text-muted-foreground">{label}</Label>
      {value ? (
        <div className="mt-2 relative">
          <img src={value} alt={label} className="w-full h-40 object-cover rounded-xl border border-border" />
          <button
            onClick={() => onChange("")}
            data-testid={`${testid}-clear`}
            className="absolute top-2 right-2 bg-white/95 hover:bg-white p-1.5 rounded-full shadow"
            aria-label="remove"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <div className="mt-2 space-y-2">
          <label
            data-testid={`${testid}-upload`}
            className="flex items-center justify-center gap-2 h-24 border-2 border-dashed border-border rounded-xl text-sm text-muted-foreground hover:border-primary hover:text-primary cursor-pointer transition-colors"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? "Processing…" : "Upload image"}
            <input type="file" accept="image/*" onChange={handleFile} className="hidden" />
          </label>
          <Input
            data-testid={`${testid}-url`}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="…or paste an image URL"
            className="h-10"
          />
        </div>
      )}
    </div>
  );
}

export default function PortfolioTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/portfolio");
      setItems(data);
    } catch {
      toast.error("Could not load portfolio");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(EMPTY); setOpen(true); };
  const openEdit = (it) => { setForm({ ...it }); setOpen(true); };

  const submit = async () => {
    if (!form.title.trim() || !form.before_image || !form.after_image) {
      toast.error("Title, before image, and after image are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        location: form.location.trim(),
        sqft: Number(form.sqft) || 0,
        before_image: form.before_image,
        after_image: form.after_image,
        span: form.span,
      };
      if (form.id) {
        await api.patch(`/portfolio/${form.id}`, payload, { withCredentials: true });
        toast.success("Updated");
      } else {
        await api.post("/portfolio", payload, { withCredentials: true });
        toast.success("Added to portfolio");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try {
      await api.delete(`/portfolio/${id}`, { withCredentials: true });
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <div data-testid="portfolio-admin-tab" className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-display text-2xl font-semibold tracking-tight">Portfolio items</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Add real before/after photos. Upload from your phone or paste image URLs.
          </p>
        </div>
        <Button onClick={openCreate} data-testid="portfolio-create-open" className="btn-primary rounded-full px-5 shrink-0">
          <Plus className="w-4 h-4 mr-2" /> New item
        </Button>
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 gap-4">
          {[1, 2].map((i) => <div key={i} className="h-48 rounded-xl bg-muted animate-pulse" />)}
        </div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center border-dashed border-border">
          <p className="text-muted-foreground">No portfolio items yet.</p>
        </Card>
      ) : (
        <div className="grid gap-3">
          {items.map((it) => (
            <Card key={it.id} data-testid={`portfolio-admin-${it.id}`} className="p-4 border-border bg-white min-w-0 overflow-hidden">
              <div className="flex flex-col sm:flex-row gap-4 min-w-0">
                <div className="flex gap-1 shrink-0">
                  <img src={it.before_image} alt="before" className="w-16 h-16 object-cover rounded-md border border-border" />
                  <img src={it.after_image} alt="after" className="w-16 h-16 object-cover rounded-md border border-border" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display text-lg font-semibold break-words">{it.title}</span>
                    <Badge variant="outline" className="capitalize">{it.span}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 break-words">
                    {it.location} · {it.sqft?.toLocaleString()} sqft
                  </p>
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2 break-words">{it.description}</p>
                </div>
                <div className="flex flex-row sm:flex-col gap-1.5 self-start sm:self-center shrink-0 flex-wrap">
                  <Button
                    size="sm" variant="outline"
                    onClick={() => openEdit(it)}
                    data-testid={`portfolio-edit-${it.id}`}
                    className="rounded-full"
                  >
                    <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
                  </Button>
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => remove(it.id, it.title)}
                    data-testid={`portfolio-delete-${it.id}`}
                    className="rounded-full text-rose-700 hover:text-rose-800 hover:bg-rose-50"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="portfolio-dialog">
          <DialogHeader>
            <DialogTitle>{form.id ? "Edit portfolio item" : "Add portfolio item"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="overline text-muted-foreground">Title</Label>
              <Input
                data-testid="portfolio-form-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Ballard Driveway Restoration"
                className="mt-2 h-11"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline text-muted-foreground">Location</Label>
                <Input
                  data-testid="portfolio-form-location"
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  placeholder="Ballard, Seattle"
                  className="mt-2 h-11"
                />
              </div>
              <div>
                <Label className="overline text-muted-foreground">Square footage</Label>
                <Input
                  data-testid="portfolio-form-sqft"
                  type="number" min={1}
                  value={form.sqft}
                  onChange={(e) => setForm({ ...form, sqft: e.target.value })}
                  className="mt-2 h-11"
                />
              </div>
            </div>
            <div>
              <Label className="overline text-muted-foreground">Description / caption</Label>
              <Textarea
                data-testid="portfolio-form-description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="What this job tackled and how it turned out."
                rows={3}
                className="mt-2"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <ImageInput
                value={form.before_image}
                onChange={(v) => setForm({ ...form, before_image: v })}
                label="Before image"
                testid="portfolio-form-before"
              />
              <ImageInput
                value={form.after_image}
                onChange={(v) => setForm({ ...form, after_image: v })}
                label="After image"
                testid="portfolio-form-after"
              />
            </div>
            <div>
              <Label className="overline text-muted-foreground">Card size in gallery</Label>
              <div className="mt-2 flex gap-2">
                {SPAN_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setForm({ ...form, span: opt.value })}
                    data-testid={`portfolio-form-span-${opt.value}`}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                      form.span === opt.value
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-white border-border text-muted-foreground hover:border-primary"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="portfolio-form-submit" className="btn-primary rounded-full px-5">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {form.id ? "Save changes" : "Add to portfolio"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
